import { useState, useMemo, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import classNames from './DeleteStudent.module.scss';
import classes from 'classnames';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import Field from '../field';
import TheButton from '@/components/school-management/TheButton/TheButton';
import Dropdown, { DropdownOption, selectedOptionType } from '../Dropdown/Dropdown';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import SVG from 'react-inlinesvg';
import { studentDetailsService } from '@/school-management/services/studentDetails';
import closeIcon from '@/assets/svg/close.svg';
import oopsIcon from '@/assets/svg/oops.svg';

const MAX_OTHER_REASON_LENGTH = 150;

interface DeleteStudentProps {
  schoolId: string;
  studentId: string;
  onClose: () => void;
  onDelete: () => void;
}

const DeleteStudent = ({ schoolId, studentId, onClose, onDelete }: DeleteStudentProps) => {
  const { t } = useAdminTranslation();
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [showStudentDeleteError, setShowStudentDeleteError] = useState<boolean>(false);
  const [isStudentDeleting, setIsStudentDeleting] = useState<boolean>(false);

  useEffect(() => {
    setSelectedReason('');
    setOtherReason('');
    setShowStudentDeleteError(false);
    setIsStudentDeleting(false);
  }, []);

  const reasonsDropdownOptions: DropdownOption[] = useMemo(() => {
    return ['requestedToBeRemoved', 'aClericalError', 'other'].map(reason => {
      return {
        value: reason,
        label: (
          <span className={classNames.peasonOption}>
            {t(`studentDataModal.deleteStudentForm.reasons.${reason}`)}
          </span>
        ),
      };
    });
  }, [t]);

  const selectedOptionView = useCallback(
    (selectedOption: selectedOptionType) => {
      return (
        <span className={classNames.selectedReason}>
          {selectedOption ? selectedOption.label : t('studentDataModal.deleteStudentForm.choose')}
        </span>
      );
    },
    [t]
  );

  const onReasonChange = useCallback((reason: any) => {
    setSelectedReason(reason as string);
  }, []);

  const onOtherTextChange = useCallback((event: any) => {
    let value = event?.target?.value || '';

    if (value.length > MAX_OTHER_REASON_LENGTH) {
      value = value.slice(0, MAX_OTHER_REASON_LENGTH);
    }

    setOtherReason(value);
  }, []);

  const onDeleteHandlebar = useCallback(async () => {
    const reason =
      selectedReason === 'other'
        ? otherReason
        : t(`studentDataModal.deleteStudentForm.reasons.${selectedReason}`);

    if (!reason || !schoolId || !studentId) {
      return;
    }

    let isOk: boolean = false;
    setIsStudentDeleting(true);

    try {
      isOk =
        (await studentDetailsService.deleteStudent(schoolId, studentId, reason))?.data?.data ||
        false;
      // Wait 2 seconds for DynamoDB stream to update postgres
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (e) {
      console.log('e', e);
    }

    setIsStudentDeleting(false);
    if (isOk) {
      onDelete();
    } else {
      setShowStudentDeleteError(true);
    }
  }, [onDelete, selectedReason, otherReason, schoolId, studentId]);

  const onTryAgainHandlebar = useCallback(() => {
    setShowStudentDeleteError(false);
  }, [setShowStudentDeleteError]);

  const modal = showStudentDeleteError ? (
    <div className={classes(classNames.modal, classNames.deletingFailed)}>
      <SVG src={oopsIcon} className={classNames.errorIcon} />
      <div className={classNames.oops}>{t('studentDataModal.deleteStudentForm.oops')}!</div>
      <h2>{t('studentDataModal.deleteStudentForm.deletingFailed')}</h2>
      <div
        className={classNames.description}
        dangerouslySetInnerHTML={{
          __html: t('studentDataModal.deleteStudentForm.deletingFailedDescription', {
            email: 'info@eb.com',
          }).toString(),
        }}
      />
      <div className={classNames.buttons}>
        <TheButton
          className={classNames.tryAgainBtn}
          text={t('studentDataModal.deleteStudentForm.tryAgain')}
          shouldRecolor={false}
          emitOnClick={onTryAgainHandlebar}
        />
      </div>
      <button
        className={classNames.closeErrorBtn}
        onClick={onClose}
        aria-label={t('studentDataModal.deleteStudentForm.close').toString()}
      >
        <SVG src={closeIcon} />
      </button>
    </div>
  ) : (
    <div className={classNames.modal}>
      <h2>{t('studentDataModal.deleteStudentForm.areYouSure')}</h2>
      <div className={classNames.description}>
        {t('studentDataModal.deleteStudentForm.description')}
      </div>
      <div className={classNames.form}>
        <Field label={t('studentDataModal.deleteStudentForm.reason').toString()}>
          <Dropdown
            value={selectedReason || t('studentDataModal.deleteStudentForm.choose')}
            options={reasonsDropdownOptions}
            onChange={onReasonChange}
            selectedOptionView={selectedOptionView}
            type='field'
          />
        </Field>
        {selectedReason === 'other' && (
          <Field label={t('studentDataModal.deleteStudentForm.why').toString()}>
            <textarea
              value={otherReason}
              onChange={onOtherTextChange}
              maxLength={MAX_OTHER_REASON_LENGTH}
            />
            <div className={classNames.otherReasonLengthLimit}>
              {otherReason.length}/{MAX_OTHER_REASON_LENGTH}
            </div>
          </Field>
        )}
      </div>
      <div className={classNames.buttons}>
        <TheButton
          className={classNames.deleteBtn}
          text={t('studentDataModal.deleteStudentForm.delete')}
          shouldRecolor={false}
          disabled={!selectedReason || (selectedReason === 'other' && !otherReason)}
          emitOnClick={onDeleteHandlebar}
        />
        <TheButton
          className={classNames.cancelBtn}
          text={t('studentDataModal.deleteStudentForm.cancel')}
          plain
          emitOnClick={onClose}
        />
      </div>
      {isStudentDeleting && (
        <div className={classNames.loader}>
          <AppLoaderCircle />
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(
    <div className={classNames.deleteStudent}>{modal}</div>,
    document.getElementById('StudentDataModalOverLayer') as Element
  );
};

export default DeleteStudent;
